import React, { useEffect, useMemo, useState } from "react";
import { api, resolvePhotoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { PackagePlus, Camera, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LAST_KEY = "admin_last_product_selection";

const emptyProduct = {
    name: "",
    description: "",
    price: "",
    discount_percent: 0,
    department: "",
    category: "",
    photo_url: "",
    is_available: true,
};

export default function AdminBulkImages() {
    const [pending, setPending] = useState([]);
    const [categories, setCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentUpload, setCurrentUpload] = useState(null);
    const [form, setForm] = useState(emptyProduct);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [b, c, d] = await Promise.all([
                api.get("/bulk-image-uploads"),
                api.get("/categories"),
                api.get("/departments"),
            ]);
            setPending(b.data);
            setCategories(c.data);
            setDepartments(d.data);
        } catch {
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const formCats = useMemo(
        () => (form.department ? categories.filter((c) => c.department === form.department) : categories),
        [categories, form.department]
    );

    const openAddProduct = (upload) => {
        setCurrentUpload(upload);
        let last = {};
        try { last = JSON.parse(localStorage.getItem(LAST_KEY) || "{}"); } catch { /* noop */ }
        const dep = last.department && departments.some((d) => d.name === last.department)
            ? last.department
            : (departments[0]?.name || "");
        const catsForDep = categories.filter((c) => c.department === dep);
        const cat = last.category && catsForDep.some((c) => c.name === last.category)
            ? last.category
            : (catsForDep[0]?.name || "");
        setForm({ ...emptyProduct, department: dep, category: cat, photo_url: upload.photo_url });
        setDialogOpen(true);
    };

    const deleteUpload = async (item) => {
        if (!window.confirm("Remove this image from the queue?")) return;
        try {
            await api.delete(`/bulk-image-uploads/${item.id}`);
            toast.success("Removed");
            setPending((prev) => prev.filter((u) => u.id !== item.id));
        } catch {
            toast.error("Could not delete");
        }
    };

    const uploadFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, photo_url: data.url }));
            toast.success("Photo uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const save = async () => {
        if (!form.name.trim() || !form.department || !form.category || !form.price) {
            toast.error("Name, price, department and category are required");
            return;
        }
        const payload = {
            name: form.name,
            description: form.description,
            price: parseFloat(form.price),
            discount_percent: parseFloat(form.discount_percent) || 0,
            department: form.department,
            category: form.category,
            photo_url: form.photo_url,
            is_available: form.is_available,
        };
        setSaving(true);
        try {
            await api.post("/products", payload);
            localStorage.setItem(LAST_KEY, JSON.stringify({
                department: form.department,
                category: form.category,
            }));
            if (currentUpload) {
                await api.post(`/bulk-image-uploads/${currentUpload.id}/mark-processed`);
            }
            toast.success("Product created");
            setDialogOpen(false);
            setCurrentUpload(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section data-testid="admin-bulk-images-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-serif text-3xl">Bulk Image Queue</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        {loading ? "Loading…" : `${pending.length} image${pending.length !== 1 ? "s" : ""} pending`}
                    </p>
                </div>
            </div>

            {loading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : pending.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl">
                    No pending images. All caught up!
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {pending.map((item) => (
                        <div
                            key={item.id}
                            className="group relative bg-card border border-border/60 rounded-2xl overflow-hidden"
                        >
                            <div className="aspect-square bg-border/10">
                                {item.photo_url ? (
                                    <img
                                        src={resolvePhotoUrl(item.photo_url)}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Camera className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                )}
                            </div>
                            <div className="p-2 flex gap-1">
                                    <Button
                                        size="sm"
                                        onClick={() => openAddProduct(item)}
                                        className="flex-1 rounded-xl bg-primary hover:bg-primary-hover text-xs"
                                    >
                                        <PackagePlus className="h-3 w-3 mr-1" />
                                        Add Product
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => deleteUpload(item)}
                                        className="rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10 px-2"
                                        title="Remove from queue"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg flex flex-col max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">New product</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Photo</Label>
                            <div className="mt-1 flex items-center gap-4">
                                <div className="h-24 w-24 rounded-2xl bg-surface/70 border border-border/60 overflow-hidden flex items-center justify-center flex-shrink-0">
                                    {form.photo_url ? (
                                        <img
                                            src={resolvePhotoUrl(form.photo_url)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Camera className="h-6 w-6 text-muted-foreground" />
                                    )}
                                </div>
                                <div className="flex-1 space-y-2">
                                    <label className="cursor-pointer inline-flex items-center gap-2 text-sm rounded-full border border-border/60 px-4 py-2 hover:border-foreground">
                                        <Upload className="h-4 w-4" />
                                        {uploading ? "Uploading…" : "Upload photo"}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            className="hidden"
                                            onChange={(e) => uploadFile(e.target.files?.[0])}
                                        />
                                    </label>
                                    <p className="text-xs text-muted-foreground">Or paste an image URL below.</p>
                                    <Input
                                        value={form.photo_url}
                                        onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                                        placeholder="https://…"
                                        className="h-9 rounded-xl text-xs"
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Name</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="mt-1 rounded-xl"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Description</Label>
                            <Textarea
                                rows={2}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                className="mt-1 rounded-xl"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em]">Price</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={form.price}
                                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                                    className="mt-1 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em]">Discount %</Label>
                                <Input
                                    type="number"
                                    step="1"
                                    min="0"
                                    max="100"
                                    value={form.discount_percent || 0}
                                    onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                                    className="mt-1 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em]">Available</Label>
                                <div className="mt-2 flex items-center h-9">
                                    <Switch
                                        checked={form.is_available}
                                        onCheckedChange={(v) => setForm({ ...form, is_available: v })}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em]">Department</Label>
                                <Select
                                    value={form.department}
                                    onValueChange={(v) => {
                                        const cats = categories.filter((c) => c.department === v);
                                        setForm({ ...form, department: v, category: cats[0]?.name || "" });
                                    }}
                                >
                                    <SelectTrigger className="mt-1 rounded-xl">
                                        <SelectValue placeholder="Pick department" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {departments.map((d) => (
                                            <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em]">Category</Label>
                                <Select
                                    value={form.category}
                                    onValueChange={(v) => setForm({ ...form, category: v })}
                                >
                                    <SelectTrigger className="mt-1 rounded-xl">
                                        <SelectValue placeholder="Pick category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {formCats.map((c) => (
                                            <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="rounded-full bg-primary hover:bg-primary-hover"
                        >
                            {saving ? "Saving…" : "Save product"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
